/**
 * Web Data Layer — shared tracking library for Webflow brand sites.
 * @version 1.0.0
 */
(function (window) {
  "use strict";

  var dedupeKeys = Object.create(null);
  var siteConfig = null;
  var observers = [];

  function ensureDataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function omitEmpty(obj) {
    var out = {};
    Object.keys(obj).forEach(function (key) {
      var val = obj[key];
      if (val !== undefined && val !== null && val !== "") {
        out[key] = val;
      }
    });
    return out;
  }

  function applySiteConfig(config) {
    config = config || {};
    siteConfig = {
      brand_name: config.brand_name,
      store_language: config.store_language,
      site_domain: config.site_domain,
      page_type: config.page_type,
      currency: config.currency,
      true_view_ms: config.true_view_ms,
      user: config.user,
    };
  }

  function getCurrency() {
    return siteConfig && siteConfig.currency;
  }

  function getTrueViewMs() {
    var ms = siteConfig && siteConfig.true_view_ms;
    return typeof ms === "number" && ms > 0 ? ms : null;
  }

  function buildEcommerce(fields) {
    return omitEmpty(Object.assign({ currency: getCurrency() }, fields));
  }

  function pushEvent(eventName, payload) {
    ensureDataLayer().push(
      omitEmpty(Object.assign({ event: eventName }, payload))
    );
  }

  function pushOnce(key, eventName, payload) {
    if (dedupeKeys[key]) {
      return false;
    }
    dedupeKeys[key] = true;
    pushEvent(eventName, payload);
    return true;
  }

  function clearEcommerce() {
    ensureDataLayer().push({ ecommerce: null });
  }

  function parseJsonAttr(el, attr) {
    try {
      var raw = el.getAttribute(attr);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw);
    } catch (_err) {
      if (window.console && console.warn) {
        console.warn("[WebDataLayer] Invalid JSON in", attr, el);
      }
      return null;
    }
  }

  function normalizeItem(item, index) {
    if (!item) {
      return null;
    }
    var normalized = omitEmpty({
      index: index !== undefined ? index : item.index,
      item_id: item.item_id,
      item_name: item.item_name,
      item_brand: item.item_brand,
      item_variant: item.item_variant || "Base",
      item_category: item.item_category,
      item_category2: item.item_category2,
      item_url: item.item_url,
      item_image_url: item.item_image_url,
      price: item.price !== undefined ? Number(item.price) : undefined,
      shelf_price:
        item.shelf_price !== undefined ? Number(item.shelf_price) : undefined,
      discount: item.discount !== undefined ? Number(item.discount) : undefined,
    });
    return Object.keys(normalized).length ? normalized : null;
  }

  function normalizePromotion(promo) {
    if (!promo) {
      return null;
    }
    return omitEmpty({
      promotion_id: promo.promotion_id,
      promotion_name: promo.promotion_name,
      creative_name: promo.creative_name,
      creative_slot: promo.creative_slot,
      location_id: promo.location_id,
      promotion_destination: promo.promotion_destination,
    });
  }

  function getPageContext(overrides) {
    var cfg = siteConfig || {};
    var lang =
      (overrides && overrides.store_language) ||
      cfg.store_language ||
      (document.documentElement && document.documentElement.lang) ||
      "en";
    return {
      brand_name: (overrides && overrides.brand_name) || cfg.brand_name,
      store_language: String(lang).toLowerCase().slice(0, 2),
      page_url: window.location.href,
      page_type:
        (overrides && overrides.page_type) ||
        cfg.page_type ||
        document.body.getAttribute("data-page-type") ||
        "other",
      page_referrer: document.referrer || "",
      site_domain:
        (overrides && overrides.site_domain) ||
        cfg.site_domain ||
        window.location.hostname,
    };
  }

  function mergeUserContext(user) {
    if (!user) {
      return {};
    }
    return omitEmpty({
      user_id: user.user_id,
      email: user.email,
      phone: user.phone,
      city: user.city,
      lead_first_name: user.lead_first_name,
      lead_last_name: user.lead_last_name,
      lead_gender: user.lead_gender,
    });
  }

  var WebDataLayer = {
    version: "1.0.0",

    /** Reset session dedupe keys (testing only). */
    resetDedupe: function () {
      dedupeKeys = Object.create(null);
    },

    /** Site-wide init: pushes global_variables. Call on every page load. */
    init: function (config) {
      applySiteConfig(config);
      var payload = Object.assign(
        getPageContext(config),
        mergeUserContext(siteConfig.user)
      );
      pushEvent("global_variables", payload);
    },

    /** Re-push global_variables when user or language changes. */
    updateContext: function (overrides) {
      overrides = overrides || {};
      var next = Object.assign({}, siteConfig || {}, overrides);
      if (overrides.user) {
        next.user = Object.assign({}, (siteConfig && siteConfig.user) || {}, overrides.user);
      }
      applySiteConfig(next);
      this.init(siteConfig);
    },

    pushCategoryClick: function (data) {
      clearEcommerce();
      pushEvent(
        "select_category",
        omitEmpty({
          category_id: data.category_id,
          category_name: data.category_name,
          subcategory_id: data.subcategory_id,
          subcategory_name: data.subcategory_name,
        })
      );
    },

    pushViewItem: function (item) {
      var normalized = normalizeItem(item, 0);
      if (!normalized) {
        return;
      }
      clearEcommerce();
      pushEvent("view_item", {
        ecommerce: buildEcommerce({
          value: normalized.price,
          items: [normalized],
        }),
      });
    },

    pushSelectItem: function (item) {
      var normalized = normalizeItem(item, item.index || 0);
      if (!normalized) {
        return;
      }
      clearEcommerce();
      pushEvent("select_item", {
        ecommerce: buildEcommerce({
          value: normalized.price,
          items: [normalized],
        }),
      });
    },

    pushViewItemList: function (listId, listName, items) {
      var normalizedItems = (items || [])
        .map(function (item, index) {
          return normalizeItem(item, index);
        })
        .filter(Boolean);
      if (!normalizedItems.length) {
        return;
      }
      clearEcommerce();
      pushEvent("view_item_list", {
        ecommerce: buildEcommerce({
          item_list_id: listId,
          item_list_name: listName,
          items: normalizedItems,
        }),
      });
    },

    pushViewPromotion: function (promo) {
      var normalized = normalizePromotion(promo);
      if (!normalized) {
        return;
      }
      var key = "view_promotion:" + normalized.promotion_id;
      if (dedupeKeys[key]) {
        return;
      }
      dedupeKeys[key] = true;
      clearEcommerce();
      pushEvent("view_promotion", {
        ecommerce: { items: [normalized] },
      });
    },

    pushSelectPromotion: function (promo) {
      var normalized = normalizePromotion(promo);
      if (!normalized) {
        return;
      }
      clearEcommerce();
      pushEvent("select_promotion", {
        ecommerce: { items: [normalized] },
      });
    },

    pushInputForm: function (data) {
      var formKey =
        "input_form:" + (data.form_type || "") + ":" + (data.form_location || "");
      pushOnce(
        formKey,
        "input_form",
        omitEmpty(Object.assign({}, data, mergeUserContext(data.user)))
      );
    },

    pushInputFormError: function (data) {
      var key =
        "input_form_error:" +
        data.form_type +
        ":" +
        data.form_field_name +
        ":" +
        data.form_field_error_message;
      pushOnce(
        key,
        "input_form_error",
        omitEmpty({
          form_type: data.form_type,
          form_location: data.form_location,
          form_field_name: data.form_field_name,
          form_field_error_message: data.form_field_error_message,
          lead_item_id: data.lead_item_id,
          lead_item_name: data.lead_item_name,
          lead_item_brand: data.lead_item_brand,
          lead_item_variant: data.lead_item_variant,
          lead_item_price: data.lead_item_price,
        })
      );
    },

    pushGenerateLead: function (data) {
      pushOnce(
        "generate_lead:" + window.location.pathname,
        "generate_lead",
        omitEmpty(
          Object.assign(
            {
              currency: getCurrency(),
              value: data.value !== undefined ? data.value : data.lead_item_price,
            },
            data,
            mergeUserContext(data)
          )
        )
      );
    },

    pushSelectDealer: function (data) {
      pushEvent(
        "select_dealer",
        omitEmpty({
          dealer_name: data.dealer_name,
          dealer_city_search: data.dealer_city_search,
          dealer_type_search: data.dealer_type_search,
          dealer_result_count: data.dealer_result_count,
        })
      );
    },

    pushLogin: function (data) {
      pushEvent(
        "login",
        omitEmpty(
          Object.assign(
            { method: data.method },
            mergeUserContext(data),
            omitEmpty({
              lead_total_quotes: data.lead_total_quotes,
            })
          )
        )
      );
    },

    pushSignUp: function (data) {
      pushOnce(
        "sign_up:" + (data.user_id || ""),
        "sign_up",
        omitEmpty(
          Object.assign(
            mergeUserContext(data),
            omitEmpty({
              account_creation_date: data.account_creation_date,
            })
          )
        )
      );
    },

    pushError: function (data) {
      var key =
        "error:" +
        (data.page_url || window.location.href) +
        ":" +
        data.error_message;
      pushOnce(
        key,
        "error",
        omitEmpty({
          error_category: data.error_category,
          error_message: data.error_message,
          error_code: data.error_code,
          page_type: data.page_type || getPageContext().page_type,
          page_url: data.page_url || window.location.href,
        })
      );
    },

    /** Wire DOM elements marked with data-track-* attributes. */
    bindPage: function (options) {
      options = options || {};
      var root = options.root || document;

      this._bindCategoryClicks(root);
      this._bindVehicleCards(root);
      this._bindPromotions(root);
      this._bindForms(root);
      this._bindDealers(root);
      this._bindTrueViewLists(root, options);
      this._bindTrueViewPromotions(root);

      if (options.viewItem) {
        this.pushViewItem(options.viewItem);
      }

      if (options.generateLead) {
        this.pushGenerateLead(options.generateLead);
      }

      if (options.error) {
        this.pushError(options.error);
      }
    },

    destroy: function () {
      observers.forEach(function (entry) {
        if (entry.timer) {
          clearTimeout(entry.timer);
        }
        if (entry.observer) {
          entry.observer.disconnect();
        }
      });
      observers = [];
    },

    _bindCategoryClicks: function (root) {
      root.querySelectorAll("[data-track-category]").forEach(function (el) {
        el.addEventListener("click", function () {
          var data = parseJsonAttr(el, "data-track-category");
          if (data) {
            WebDataLayer.pushCategoryClick(data);
          }
        });
      });
    },

    _bindVehicleCards: function (root) {
      root.querySelectorAll("[data-track-item]").forEach(function (el) {
        el.addEventListener("click", function () {
          var data = parseJsonAttr(el, "data-track-item");
          if (data) {
            WebDataLayer.pushSelectItem(data);
          }
        });
      });
    },

    _bindPromotions: function (root) {
      root.querySelectorAll("[data-track-promo]").forEach(function (el) {
        el.addEventListener("click", function () {
          var data = parseJsonAttr(el, "data-track-promo");
          if (data) {
            WebDataLayer.pushSelectPromotion(data);
          }
        });
      });
    },

    _bindForms: function (root) {
      root.querySelectorAll("form[data-track-form]").forEach(function (form) {
        var formType = form.getAttribute("data-track-form");
        var formLocation =
          form.getAttribute("data-track-form-location") || window.location.pathname;
        var leadData = parseJsonAttr(form, "data-track-lead-item") || {};
        var started = false;

        form.addEventListener(
          "focusin",
          function () {
            if (started) {
              return;
            }
            started = true;
            WebDataLayer.pushInputForm(
              Object.assign(
                {
                  form_type: formType,
                  form_location: formLocation,
                },
                leadData
              )
            );
          },
          true
        );

        form.querySelectorAll("[data-track-field-error]").forEach(function (field) {
          field.addEventListener("invalid", function () {
            WebDataLayer.pushInputFormError(
              Object.assign(
                {
                  form_type: formType,
                  form_location: formLocation,
                  form_field_name: field.name || field.id,
                  form_field_error_message: field.validationMessage,
                },
                leadData
              )
            );
          });
        });
      });
    },

    _bindDealers: function (root) {
      root.querySelectorAll("[data-track-dealer]").forEach(function (el) {
        el.addEventListener("click", function () {
          var data = parseJsonAttr(el, "data-track-dealer");
          if (data) {
            WebDataLayer.pushSelectDealer(data);
          }
        });
      });
    },

    _bindTrueViewLists: function (root) {
      var trueViewMs = getTrueViewMs();
      if (!trueViewMs) {
        return;
      }

      root.querySelectorAll("[data-track-item-list]").forEach(function (container) {
        var listId = container.getAttribute("data-track-item-list");
        var listName =
          container.getAttribute("data-track-item-list-name") || listId;
        var entry = { observer: null, timer: null, fired: false };

        entry.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (obsEntry) {
              if (entry.fired) {
                return;
              }
              if (obsEntry.isIntersecting) {
                if (!entry.timer) {
                  entry.timer = setTimeout(function () {
                    if (entry.fired) {
                      return;
                    }
                    entry.fired = true;
                    var items = [];
                    container
                      .querySelectorAll("[data-track-item]")
                      .forEach(function (el, index) {
                        var item = parseJsonAttr(el, "data-track-item");
                        if (item) {
                          item.index = index;
                          items.push(item);
                        }
                      });
                    WebDataLayer.pushViewItemList(listId, listName, items);
                  }, trueViewMs);
                }
              } else if (entry.timer) {
                clearTimeout(entry.timer);
                entry.timer = null;
              }
            });
          },
          { threshold: 0.5 }
        );

        entry.observer.observe(container);
        observers.push(entry);
      });
    },

    _bindTrueViewPromotions: function (root) {
      var trueViewMs = getTrueViewMs();
      if (!trueViewMs) {
        return;
      }

      root.querySelectorAll("[data-track-promo]").forEach(function (el) {
        var entry = { observer: null, timer: null, fired: false };

        entry.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (obsEntry) {
              if (entry.fired) {
                return;
              }
              if (obsEntry.isIntersecting) {
                if (!entry.timer) {
                  entry.timer = setTimeout(function () {
                    if (entry.fired) {
                      return;
                    }
                    entry.fired = true;
                    var data = parseJsonAttr(el, "data-track-promo");
                    if (data) {
                      WebDataLayer.pushViewPromotion(data);
                    }
                  }, trueViewMs);
                }
              } else if (entry.timer) {
                clearTimeout(entry.timer);
                entry.timer = null;
              }
            });
          },
          { threshold: 0.5 }
        );

        entry.observer.observe(el);
        observers.push(entry);
      });
    },
  };

  window.WebDataLayer = WebDataLayer;
})(window);
